import type { ProjectState } from "hooks/useProjects/useProjects.types";

export const productionTypeMapping: Record<string, string> = {
  short: "Short",
  feature: "Independent",
  documentary: "Documentary",
  studio: "Studio",
  commercial: "Commercial",
  other: "Other",
};

/** First grapheme of the first two words, safe for emoji/surrogate pairs. */
export const getProjectMonogram = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? "")
    .join("");

export const getProductionTypeEdgeColor = (productionType: string) => {
  switch (productionType) {
    case "short":
      return "bg-hue-cyan";
    case "feature":
      return "bg-hue-violet";
    case "studio":
      return "bg-primary";
    case "documentary":
      return "bg-hue-emerald";
    case "commercial":
      return "bg-hue-blue";
    default:
      return "bg-muted-foreground/40";
  }
};

export const isLimitedProject = (project: {
  isLicensed?: boolean;
  isTrial?: boolean;
  purchaseType?: string;
}) => project.isLicensed === false || Boolean(project.isTrial) || project.purchaseType === "trial";

export interface PipelineStage {
  label: string;
  done: boolean;
}

type PipelineProject = Pick<ProjectState, "featureStatuses" | "activeDocumentId" | "aiCredits">;

const PIPELINE_STAGES = [
  { label: "Breakdown", feature: "script_breakdown", stale: "script_breakdown_stale" },
  { label: "Characters", feature: "character_breakdown", stale: "character_breakdown_stale" },
  { label: "Schedule", feature: "schedule", stale: "schedule_stale" },
  { label: "Budget", feature: "budget", stale: "budget_stale" },
  { label: "Coverage", feature: "coverage", stale: "coverage_stale" },
] as const;

export const getPipelineStages = (project: PipelineProject | undefined): PipelineStage[] =>
  PIPELINE_STAGES.map(({ label, feature, stale }) => ({
    label,
    done:
      !project?.aiCredits?.staleness?.[stale] &&
      Boolean(
        project?.featureStatuses?.some((status) => {
          const metadata = status.metadata;
          const placeholder =
            metadata !== null &&
            typeof metadata === "object" &&
            "seeded_placeholder" in metadata &&
            metadata.seeded_placeholder === true;
          return (
            status.feature === feature &&
            status.status === "completed" &&
            !placeholder &&
            (!project.activeDocumentId || status.documentId === project.activeDocumentId)
          );
        })
      ),
  }));

export interface NextStep {
  /** What to do, e.g. "Build the schedule". */
  title: string;
  /** Button text, e.g. "Open schedule". */
  cta: string;
  /** Path inside the project, e.g. "schedule/stripboard". */
  path: string;
  /** True when every stage has current completed work. */
  allDone: boolean;
}

// Same order as getPipelineStages. Opening a module never starts an AI run.
const NEXT_STEPS: Array<Omit<NextStep, "allDone">> = [
  { title: "Break down the script", cta: "Open breakdown", path: "script/breakdown" },
  { title: "Meet your characters", cta: "Open characters", path: "characters/breakdown" },
  { title: "Build the schedule", cta: "Open schedule", path: "schedule/stripboard" },
  { title: "Draft the budget", cta: "Open budget", path: "budget" },
  { title: "Get script coverage", cta: "Open coverage", path: "script/coverage" },
];

/** The first stage without current completed work, or "open project" when all are done. */
export const getNextStep = (project: Parameters<typeof getPipelineStages>[0]): NextStep => {
  const index = getPipelineStages(project).findIndex((stage) => !stage.done);
  if (index === -1) {
    return {
      title: "Every stage is complete",
      cta: "Open project",
      path: "script/breakdown",
      allDone: true,
    };
  }
  return { ...NEXT_STEPS[index], allDone: false };
};

export const getProjectPath = (workspaceId: string, projectId: string, path: string) =>
  `/workspaces/${workspaceId}/projects/${projectId}/${path}`;

export const getProductionTypeBadgeColor = (productionType: string) => {
  switch (productionType) {
    case "short":
      return "bg-hue-cyan/10 text-hue-cyan border-hue-cyan/25";
    case "feature":
      return "bg-hue-violet/10 text-hue-violet border-hue-violet/25";
    case "studio":
      return "bg-primary/10 text-primary border-primary/20";
    case "documentary":
      return "bg-hue-emerald/10 text-hue-emerald border-hue-emerald/25";
    case "commercial":
      return "bg-hue-blue/10 text-hue-blue border-hue-blue/25";
    default:
      return "bg-muted/60 text-foreground/80 border-border";
  }
};
